import { Component, computed, inject, input, OnDestroy, OnInit, output, signal, HostListener } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { HtmlSplitterHelper } from 'src/app/shared/helpers/html-splitter.helper';
import { HtmlToMarkdownHelper } from 'src/app/shared/helpers/html-to-markdown.helper';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { InsertOptionsMenuComponent } from '../insert-options-menu/insert-options-menu.component';
import { isFileAllowed } from 'src/app/shared/utils/file-validation.utils';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ChunkService } from '../chunk/chunk.service';
import { ContentBuilderDraft, ContentBuilderVersion, ContentGeneratorService } from '../content-generator.service';
import { ContentStateService } from '../content-state.service';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { VoiceRecorderButtonComponent } from 'src/app/shared/components/recorders/voice-recorder-button/voice-recorder-button.component';
import { TemplatePickerComponent } from 'src/app/shared/components/template-picker/template-picker.component';
import { VersionTileComponent } from 'src/app/shared/components/ui/version-tile/version-tile.component';
import { VoiceNoteCardComponent } from 'src/app/shared/components/ui/voice-note-card/voice-note-card.component';
import { ContentSearcherComponent, ContentSearchResult } from '../content-searcher/content-searcher.component';

@Component({
  selector: 'app-content-refiner',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    TranslateModule,
    TemplatePickerComponent,
    VersionTileComponent,
    VoiceRecorderButtonComponent,
    VoiceNoteCardComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './content-refiner.component.html',
  styleUrls: ['./content-refiner.component.scss'],
})
export class ContentRefinerComponent implements OnInit, OnDestroy {
  draft = input.required<ContentBuilderDraft>();
  close = output<void>();
  updated = output<ContentBuilderDraft>();

  private contentGeneratorService = inject(ContentGeneratorService);
  private confirmMenuService = inject(ConfirmMenuService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private contentStateService = inject(ContentStateService);
  private translate = inject(TranslateService);
  private errorHandler = inject(ErrorHandlerService);

  private destroy$ = new Subject<void>();

  instruction = '';
  templateId = 'none';
  isGenerating = signal(false);
  isInserting = signal(false);

  isMobile = signal(window.innerWidth < 599);

  @HostListener('window:resize')
  onResize() {
    this.isMobile.set(window.innerWidth < 599);
  }

  ngOnInit() {
    const draftTemplateId = this.draft()?.templateId;
    if (draftTemplateId !== undefined && draftTemplateId !== null) {
      this.templateId = draftTemplateId;
    } else {
      this.templateId = localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none';
    }
  }

  isRecordingVoice = signal(false);

  attachedFiles = signal<{ id: string; file?: File; url?: string; name: string; type: string; uploading: boolean }[]>([]);
  attachedExternalContexts = signal<ContentSearchResult[]>([]);
  isDraggingOver = signal(false);
  dragCounter = 0;

  sortedVersions = computed(() => {
    const versions = this.draft().versions || [];
    return [...versions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  });

  currentVersionId = computed(() => this.draft().currentVersionId);
  lastUpdated = computed(() => this.draft().updatedAt);

  getCurrentContent(): string {
    const version = this.draft().versions.find((v) => v._id === this.currentVersionId());
    return version ? version.content : this.draft().content || '';
  }

  refineContent() {
    const hasTemplate = this.templateId && this.templateId !== 'none';
    if ((!this.instruction && !hasTemplate) || this.isGenerating()) return;

    if (this.attachedFiles().some((f) => f.uploading)) {
      this.toast.snackbar.open(this.translate.instant('ContentGenerator.WaitUpload') || 'Please wait for uploads to finish', 'Close');
      return;
    }

    this.isGenerating.set(true);
    // If no explicit instruction but template is selected, use a generic one
    const finalInstruction = this.instruction || 'Apply the selected template style.';

    const mediaContexts = this.attachedFiles()
      .filter((f) => !f.uploading && f.url)
      .map((f) => ({
        url: f.url!,
        type: f.type as 'audio' | 'image' | 'video' | 'file',
        filename: f.name,
      }));

    this.contentGeneratorService
      .addVersion(
        this.draft()._id!,
        finalInstruction,
        this.draft().content,
        'fast',
        this.templateId,
        mediaContexts.length ? mediaContexts : undefined,
        this.attachedExternalContexts().map((c) => c.id)
      )
      .subscribe({
        next: (updatedDraft) => {
          this.updated.emit(updatedDraft);
          this.instruction = '';
          this.attachedFiles.set([]);
          this.attachedExternalContexts.set([]);
          this.isGenerating.set(false);
        },
        error: (err) => {
          console.error('Failed to refine content', err);
          this.errorHandler.handleAiRestrictedError(err);
          this.isGenerating.set(false);
        },
      });
  }

  // Array of file methods for drag-and-drop & uploads
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    if (this.dragCounter === 1) {
      this.isDraggingOver.set(true);
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDraggingOver.set(false);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDraggingOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  async handleFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!isFileAllowed(file)) {
        this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
        continue;
      }

      let mediaType: 'audio' | 'file' | 'video' | 'image' = 'file';
      if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('image/')) mediaType = 'image';

      const fileId = Date.now().toString() + '_' + i;

      this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: mediaType, uploading: true }]);

      this.uploadFileInternal(file, fileId);
    }
  }

  async onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    this.handleFiles(files);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const filename = `content_generator_attachments/${this.contentStateService.courseId || this.contentStateService.noteId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, uploading: false } : f)));
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
  }

  removeExternalContext(id: string) {
    this.attachedExternalContexts.update((contexts) => contexts.filter((c) => c.id !== id));
  }

  private cdkOverlay = inject(Overlay);
  private searcherOverlayRef: OverlayRef | null = null;

  toggleContentSearcher() {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }

    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().centerVertically();

    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);

    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => this.onContentSearcherSelect(result));
    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });

    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async onContentSearcherSelect(result: ContentSearchResult) {
    if (!result?.id) return;
    if (this.attachedExternalContexts().find((c) => c.id === result.id)) return;

    this.attachedExternalContexts.update((contexts) => [...contexts, result]);
  }

  restoreVersion(version: ContentBuilderVersion | null) {
    if (this.isGenerating()) return;

    if (!version) return;

    this.contentGeneratorService.restoreVersion(this.draft()._id!, version._id).subscribe({
      next: (updatedDraft) => {
        this.updated.emit(updatedDraft);
      },
      error: (err) => {
        console.error('Failed to restore version', err);
      },
    });
  }

  deleteVersion(trigger: HTMLElement, version: ContentBuilderVersion) {
    this.confirmMenuService.confirm(trigger, 'ContentRefiner.DeleteVersionConfirm', 'delete').subscribe(() => {
      this.contentGeneratorService.deleteVersion(this.draft()._id!, version._id).subscribe({
        next: (updatedDraft) => {
          this.updated.emit(updatedDraft);
        },
        error: (err) => {
          console.error('Failed to delete version', err);
        },
      });
    });
  }

  confirmInsert(trigger: HTMLElement) {
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(
      trigger,
      InsertOptionsMenuComponent
    );

    const sub = componentInstance.optionSelected.subscribe((option: 'chunks' | 'canvas' | 'markdown') => {
      this.dynamicOverlayService.close();
      this.insertContent(option);
    });

    overlayRef.detachments().subscribe(() => {
      sub.unsubscribe();
    });
  }

  async insertContent(insertMode: 'chunks' | 'canvas' | 'markdown' = 'chunks') {
    if (this.isInserting()) return;
    this.isInserting.set(true);

    const content = this.getCurrentContent();

    if (!content.trim()) {
      this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
      this.isInserting.set(false);
      return;
    }

    try {
      const userData = this.authService.userInfo();
      const safeUser = userData || JSON.parse(localStorage.getItem('userData') || '{}');

      let chunkPayloads: any[];

      if (insertMode === 'chunks') {
        const chunks = HtmlSplitterHelper.split(content);
        if (chunks.length === 0) {
          this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
          this.isInserting.set(false);
          return;
        }

        chunkPayloads = chunks.map((chunkHtml) => ({
          _id: Date.now().toString() + Math.random().toString().slice(2, 8), // Temp ID
          content: chunkHtml,
          type: 'html',
          language: this.translate.currentLang, // or detect from draft
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId, // Need to make sure this is available if needed, or rely on context
          contentName: this.draft().title || 'Generated Content', // Or draft title
        }));
      } else if (insertMode === 'markdown') {
        chunkPayloads = [{
          _id: Date.now().toString() + Math.random().toString().slice(2, 8), // Temp ID
          content: HtmlToMarkdownHelper.convert(content),
          type: 'code',
          codeLanguage: 'markdown',
          language: this.translate.currentLang, // or detect from draft
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId, // Need to make sure this is available if needed, or rely on context
          title: this.draft().title || 'Markdown Content',
          contentName: this.draft().title || 'Generated Content', // Or draft title
        }];
      } else {
        chunkPayloads = [{
          _id: Date.now().toString() + Math.random().toString().slice(2, 8), // Temp ID
          content: content,
          type: 'canvas-transcript',
          language: this.translate.currentLang, // or detect from draft
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId, // Need to make sure this is available if needed, or rely on context
          title: this.draft().title || 'Transcription',
          contentName: this.draft().title || 'Generated Content', // Or draft title
        }];
      }

      await firstValueFrom(
        this.chunkService.createChunk({
          chunks: chunkPayloads,
          insertBelowID: undefined, // Or handle insertion point
        })
      );

      this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
      this.close.emit(); // Close the refiner/draft view
    } catch (e) {
      console.error('Failed to insert content', e);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
    } finally {
      this.isInserting.set(false);
    }
  }

  onVoiceRecorded(file: File) {
    const id = Date.now().toString();
    this.attachedFiles.update((files) => [...files, { id, file, name: file.name, type: 'audio', uploading: true }]);
    this.uploadFileInternal(file, id);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
